import { createFileRoute, notFound, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Field } from '../components/ui/Field';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Pharmacy profile view + edit — Slice 1 of the admin/pharmacy surface.
//
// Read: RLS-scoped via `pharmacies_select_own_tenant`; non-platform
// users see only their tenant's pharmacies. Pharmacies the caller
// can't see surface as a 404 (RLS-empty is indistinguishable from
// "doesn't exist", same defensive stance as the patients reveal route).
//
// Edit: writes via the SECURITY DEFINER RPC `update_pharmacy_profile`
// (migration 20260526170000). The RPC enforces the user_type +
// per-pharmacy visibility predicates and writes a
// `pharmacy.profile_updated` audit event with {before, after} diff.
// Application code does NOT direct-update the pharmacies table —
// there's no UPDATE RLS policy by design (the RPC is the sole writer,
// which keeps the audit trail consistent).

const PHARMACY_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isPharmacyIdParam(value: unknown): value is string {
  return typeof value === 'string' && PHARMACY_ID_UUID_RE.test(value);
}

const DETAIL_COLUMNS = 'id, name, address, city, state, zip, timezone';

export type PharmacyDetailRow = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  timezone: string;
};

// Editable fields. These match the RPC's whitelist exactly; any drift
// produces an LYNRX_INVALID_FIELD at save time.
const EDITABLE_FIELDS = ['name', 'address', 'city', 'state', 'zip', 'timezone'] as const;
type EditableField = (typeof EDITABLE_FIELDS)[number];

type FormState = Record<EditableField, string>;

function rowToForm(row: PharmacyDetailRow): FormState {
  return {
    name: row.name,
    address: row.address ?? '',
    city: row.city ?? '',
    state: row.state ?? '',
    zip: row.zip ?? '',
    timezone: row.timezone,
  };
}

// Build the JSONB partial-patch payload from the current form against
// the loaded row. Empty strings on optional fields become JSON null
// (RFC 7396 clears the column); empty strings on required fields
// (name, timezone) get sent as empty — the RPC's LYNRX_REQUIRED_FIELD
// guard catches them with a clean error message.
function buildPatch(initial: PharmacyDetailRow, form: FormState): Record<string, string | null> {
  const patch: Record<string, string | null> = {};
  for (const field of EDITABLE_FIELDS) {
    const currentRaw = (initial[field] ?? '').trim();
    const nextRaw = form[field].trim();
    if (currentRaw === nextRaw) continue;
    // Required fields: send as-is (empty triggers RPC error). Optional
    // fields: empty → null (clears the column).
    if (field === 'name' || field === 'timezone') {
      patch[field] = nextRaw;
    } else {
      patch[field] = nextRaw === '' ? null : nextRaw;
    }
  }
  return patch;
}

// RPC error → user-friendly message. RPC raises strings that start
// with LYNRX_<CODE>: <message>. Anything else is a generic save error.
function friendlyError(message: string | undefined): string {
  if (!message) return 'Could not save the pharmacy profile.';
  if (message.includes('LYNRX_PHARMACY_NO_CHANGES'))
    return 'No changes to save.';
  if (message.includes('LYNRX_REQUIRED_FIELD'))
    return 'Name and timezone are required.';
  if (message.includes('LYNRX_FORBIDDEN'))
    return 'Your account is not authorized to edit pharmacy profiles.';
  if (message.includes('LYNRX_PHARMACY_NOT_FOUND'))
    return 'This pharmacy is not accessible to your account.';
  if (message.includes('LYNRX_INVALID_FIELD'))
    return 'One of the submitted fields is not editable.';
  return 'Could not save the pharmacy profile.';
}

export const Route = createFileRoute('/_authenticated/admin/pharmacy/$pharmacyId')({
  component: PharmacyDetailPage,
  parseParams: (params): { pharmacyId: string } => {
    if (!isPharmacyIdParam(params.pharmacyId)) {
      throw notFound();
    }
    return { pharmacyId: params.pharmacyId };
  },
});

function PharmacyDetailPage(): JSX.Element {
  const { pharmacyId } = Route.useParams();
  return <PharmacyDetailView pharmacyId={pharmacyId} />;
}

type ViewProps = {
  pharmacyId: string;
  // Test seam — defaults to the singleton browser supabase client.
  client?: LynrxSupabaseClient;
};

export function PharmacyDetailView({
  pharmacyId,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  useSetPageHeader({
    title: 'Pharmacy profile',
    subtitle: 'View and edit profile fields. Changes are audit-logged.',
  });

  const [row, setRow] = useState<PharmacyDetailRow | null | undefined>(undefined);
  const [form, setForm] = useState<FormState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const fetchRow = useCallback(async () => {
    if (!session) return;
    setLoadError(null);
    const { data, error } = await client
      .from('pharmacies')
      .select(DETAIL_COLUMNS)
      .eq('id', pharmacyId)
      .is('deleted_at', null)
      .maybeSingle();
    if (error) {
      captureSupabaseError(error, 'admin.pharmacy.detail.fetch');
      setLoadError('Could not load this pharmacy.');
      return;
    }
    if (!data) {
      setRow(null);
      return;
    }
    const loaded = data as unknown as PharmacyDetailRow;
    setRow(loaded);
    setForm(rowToForm(loaded));
  }, [client, session, pharmacyId]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchRow();
  }, [isReady, session, fetchRow]);

  const setField = useCallback(
    (field: EditableField, value: string) => {
      setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
      setSavedAt(null);
      setSaveError(null);
    },
    [],
  );

  const dirty = useMemo(() => {
    if (!row || !form) return false;
    return Object.keys(buildPatch(row, form)).length > 0;
  }, [row, form]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!row || !form) return;
      const patch = buildPatch(row, form);
      if (Object.keys(patch).length === 0) {
        setSaveError('No changes to save.');
        return;
      }
      setSaving(true);
      setSaveError(null);
      setSavedAt(null);
      try {
        const { error } = await client.rpc('update_pharmacy_profile', {
          p_pharmacy_id: pharmacyId,
          p_updates: patch,
        });
        if (error) {
          captureSupabaseError(error, 'admin.pharmacy.detail.save');
          setSaveError(friendlyError(error.message));
          return;
        }
        // Re-fetch so the loaded baseline matches the saved state —
        // a follow-up edit then computes the diff against the new
        // values rather than the stale pre-save snapshot.
        await fetchRow();
        setSavedAt(Date.now());
      } catch (err) {
        captureSupabaseError(err, 'admin.pharmacy.detail.save.exception');
        setSaveError(friendlyError(undefined));
      } finally {
        setSaving(false);
      }
    },
    [client, fetchRow, form, pharmacyId, row],
  );

  if (row === undefined) {
    return <p className="text-sm text-ink-2">Loading pharmacy…</p>;
  }
  if (loadError) {
    return <Alert tone="error">{loadError}</Alert>;
  }
  if (row === null) {
    return (
      <Card>
        <p className="text-sm text-ink">Pharmacy not found.</p>
        <p className="mt-2 text-sm text-ink-2">
          The pharmacy you're looking for doesn't exist or isn't accessible
          to your account.{' '}
          <Link to="/admin/pharmacy" className="underline underline-offset-4">
            Back to pharmacies
          </Link>
          .
        </p>
      </Card>
    );
  }
  if (!form) {
    return <p className="text-sm text-ink-2">Loading pharmacy…</p>;
  }

  return (
    <Card>
      <div className="mb-3 text-sm">
        <Link
          to="/admin/pharmacy"
          className="text-ink-2 underline-offset-4 hover:underline"
        >
          ← All pharmacies
        </Link>
      </div>

      {saveError ? (
        <div className="mb-4">
          <Alert tone="error">{saveError}</Alert>
        </div>
      ) : null}
      {savedAt && !saveError ? (
        <div className="mb-4">
          <Alert tone="success">Pharmacy profile saved.</Alert>
        </div>
      ) : null}

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <Field
            label="Name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Address"
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
          />
        </div>
        <Field
          label="City"
          value={form.city}
          onChange={(e) => setField('city', e.target.value)}
        />
        <Field
          label="State"
          value={form.state}
          onChange={(e) => setField('state', e.target.value)}
        />
        <Field
          label="ZIP"
          value={form.zip}
          onChange={(e) => setField('zip', e.target.value)}
        />
        <Field
          label="Timezone"
          value={form.timezone}
          onChange={(e) => setField('timezone', e.target.value)}
          hint="IANA timezone (e.g. America/New_York)"
          required
        />

        <div className="sm:col-span-2 mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setForm(rowToForm(row))}
            disabled={saving || !dirty}
          >
            Revert
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
